import {
  Box,
  Display,
  Col,
  DisplayPosition,
  Grid,
  Space,
  FlexDirection,
  JustifyContent,
  Image,
  useTheme,
  withCitrusTheme,
  Color,
  ZIndex,
  Overflow,
} from '@gousto-internal/citrus-react'
import React from 'react'

import {
  CTABlock,
  EnvironmentalBenefitsCopy,
  EnvironmentalBenefitsCopyFirstAndThirdBold,
  EnvironmentalBenefitsCopyWithUrl,
  HeaderSection,
} from './components'

import BottomChickPeas from '../../assets/EnvironmentalBenefitsSectionAssets/BottomChickPeas.png'
import TopChickPeas from '../../assets/EnvironmentalBenefitsSectionAssets/TopChickPeas.png'
import XWideChickPeas from '../../assets/EnvironmentalBenefitsSectionAssets/XWideChickPeas.png'
import CookingTogetherMobileWebp from '../../assets/EnvironmentalBenefitsSectionAssets/cooking-together-mobile.webp'
import CookingTogetherDesktopWebp from '../../assets/EnvironmentalBenefitsSectionAssets/cooking-together-desktop.webp'
import CookingTogetherDesktopJpg from '../../assets/EnvironmentalBenefitsSectionAssets/cooking-together-desktop.jpg'

import {
  ENVIRONMENTAL_BENEFITS_BULLET_ONE,
  ENVIRONMENTAL_BENEFITS_BULLET_TWO,
  ENVIRONMENTAL_BENEFITS_BULLET_THREE_WITH_URL,
} from '../../config'

export type EnvironmentalBenefitsSectionProps = {
  signupCTA: string
  onCTAClick: () => void
}

function EnvironmentalBenefits({ signupCTA, onCTAClick }: EnvironmentalBenefitsSectionProps) {
  const { theme } = useTheme()
  const breakpointToMedium = `@media (max-width: ${theme.tokens.breakpoints[1]})`
  const breakpointToLarge = `@media (max-width: ${theme.tokens.breakpoints[2]})`
  const breakpointFromXL = `@media (min-width: ${theme.tokens.breakpoints[3]})`

  const GridWithBackgroundPattern = withCitrusTheme(Grid, () => ({
    [breakpointToMedium]: {
      backgroundImage: `url(${TopChickPeas})`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'top right',
    },
  }))

  const MainImage = withCitrusTheme(Image, () => ({
    [breakpointToLarge]: { maxHeight: '650px' },
    [breakpointFromXL]: { maxHeight: '560px' },
  }))

  return (
    <section
      data-testid="environmentalBenefits"
      style={{ backgroundColor: `${theme.tokens.colors[Color.NeutralGrey_50]}` }}
    >
      <Box overflowX={Overflow.Hidden}>
        <GridWithBackgroundPattern>
          <Col
            size={[12, null, 6, null]}
            display={Display.Flex}
            paddingV={16}
            paddingH={[8, 20, 4, 8]}
            justifyContent={JustifyContent.FlexStart}
          >
            <Box zIndex={ZIndex.Masked} width={[null, null, '100%', '80%']}>
              <HeaderSection />
              <Space size={4} />
              <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
                <EnvironmentalBenefitsCopy
                  ENVIRONMENTAL_BENEFITS_BULLET={ENVIRONMENTAL_BENEFITS_BULLET_ONE}
                />
                <Space size={2} />
                <EnvironmentalBenefitsCopyFirstAndThirdBold
                  ENVIRONMENTAL_BENEFITS_BULLET={ENVIRONMENTAL_BENEFITS_BULLET_TWO}
                />
                <Space size={2} />
                <EnvironmentalBenefitsCopyWithUrl
                  ENVIRONMENTAL_BENEFITS_BULLET_WITH_URL={
                    ENVIRONMENTAL_BENEFITS_BULLET_THREE_WITH_URL
                  }
                />
              </Box>
              <Space size={4} />
              <CTABlock signupCTA={signupCTA} onCTAClick={onCTAClick} />
              <Space size={4} />
            </Box>
          </Col>
          <Col
            size={[12, null, 6, null]}
            position={DisplayPosition.Relative}
            height={['400px', null, 'auto', null]}
          >
            <Box
              position={DisplayPosition.Absolute}
              left={['10%', null, '-65%', null]}
              top={['-120px', null, 'initial']}
              height={'100%'}
              zIndex={ZIndex.Default}
            >
              <Box display={[Display.None, null, Display.Block, null]} height={'100%'}>
                <Image src={XWideChickPeas} srcSet={XWideChickPeas} alt="" />
              </Box>
              <Box display={[Display.Block, null, Display.None, null]}>
                <Image src={BottomChickPeas} srcSet={BottomChickPeas} alt="" />
              </Box>
            </Box>
            <picture>
              <source
                srcSet={CookingTogetherMobileWebp}
                media={`(max-width: ${theme.tokens.breakpoints[1]})`}
                type="image/webp"
              />
              <source
                srcSet={CookingTogetherDesktopWebp}
                media={`(min-width: ${theme.tokens.breakpoints[1]})`}
                type="image/webp"
              />
              <MainImage
                src={CookingTogetherDesktopJpg}
                alt="Couple cooking a Spicy Sichuan-style Veggie Noodle Bowl Gousto recipe"
                width={['100%', null, '50vw', null]}
                style={{ objectFit: 'cover' }}
              />
            </picture>
          </Col>
        </GridWithBackgroundPattern>
      </Box>
    </section>
  )
}

export const EnvironmentalBenefitsSection = EnvironmentalBenefits
